import { fourFConfig, modeMeaning, moodLevel, schemaMeaning, sudsLabel } from "./config";
import { copingStyleLabel, needStatements, schemaScript } from "./schemaFlip";

const mood = (v, note) => {
  const m = moodLevel(v);
  return m ? `${m.label} (${v > 0 ? "+" : ""}${v} on a -10 to +10 scale — "${m.descriptor}")${note ? `; they wrote: "${note}"` : ""}` : null;
};
const suds = (v) => (v == null ? null : `${v}/10 (${sudsLabel(v)})`);
const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v != null && v !== "" && !(Array.isArray(v) && !v.length)));

// Human-readable, labelled picture of everything the person has shared so far.
export function buildAiContext(s) {
  const f = s.survivalResponse ? fourFConfig(s.survivalResponse) : null;
  const drop = s.preSuds != null && s.postSuds != null ? s.preSuds - s.postSuds : null;
  const lift = s.moodBefore != null && s.moodNow != null ? s.moodNow - s.moodBefore : null;
  return clean({
    "How they arrived": s.path === "emergency"
      ? `Distress was high enough that they pressed "Get help now" and did a guided calming-breath exercise with a soothing voice${s.emergencySeconds ? ` for about ${Math.max(1, Math.round(s.emergencySeconds / 60))} minute(s)` : ""}`
      : s.moodOnly ? "Logging their mood" : "A regular check-in, within their window of tolerance",
    "Time of check-in": s.when,
    "Distress at check-in (SUDS)": suds(s.preSuds),
    "Distress now (SUDS)": suds(s.postSuds),
    "Change in distress": drop == null ? null : drop > 0 ? `dropped by ${drop} point(s)` : drop < 0 ? `rose by ${-drop} point(s)` : "unchanged",
    "Mood as a result of the trigger (caused by the trigger, not how they felt beforehand)": mood(s.moodBefore, s.moodBeforeNote),
    "Were they triggered": s.triggered == null ? null : s.triggered ? `Yes${s.triggeredAt ? `, at ${new Date(s.triggeredAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}` : "No",
    "What triggered them (their words)": s.triggerNote ? `"${s.triggerNote}"` : null,
    "Survival response (body)": f ? `${f.label} — ${f.blurb}` : null,
    "Coping mode identified": s.copingMode ? `${s.copingMode}${modeMeaning(s.copingMode) ? ` — ${modeMeaning(s.copingMode)}` : ""}` : null,
    "Schema state identified": s.schemaState ? `${s.schemaState}${schemaMeaning(s.schemaState) ? ` — ${schemaMeaning(s.schemaState)}` : ""}` : null,
    "Mood right now": mood(s.moodNow, s.moodNote),
    "Change in mood": lift == null ? null : lift > 0 ? `lifted by ${lift} point(s)` : lift < 0 ? `dropped by ${-lift} point(s)` : "unchanged",
    "Unmet needs they ticked": needStatements(s.needs || []),
    "Schemas they recognised in their inner voice": (s.flips || []).map((x) => {
      const sc = schemaScript(x.schema);
      return `${x.schema}${sc ? ` (inner voice: "${sc.script}")` : ""}${x.copingStyle ? ` — what they did: ${copingStyleLabel(x.copingStyle)}` : ""}`;
    }),
    "Their Flip the Script work": (s.flips || []).flatMap((x) => [
      x.story && `${x.schema} — the story the schema told: "${x.story}"`,
      x.facts && `${x.schema} — the facts: "${x.facts}"`,
      x.ownScript && `${x.schema} — their own Healthy Adult script: "${x.ownScript}"`,
      x.action && `${x.schema} — small opposite action chosen: "${x.action}"`,
    ].filter(Boolean)),
    "Coping strategies used so far": (s.coping || []).filter(Boolean),
  });
}
